import { useContext, useState } from 'react';
import { type SkuAnnotationData, type SkuAnnotations, SkuAnnotationType } from '@rta/rta-blueprint-library';

import { AppContext } from '@r-app/app-context';
import { getAnnotationBottomExtent, getAnnotationLeftExtent, getAnnotationRightExtent, getAnnotationTopExtent, placeAnnotationPreset } from '../helpers/place-annotation-preset';
import { useSkuAnnotation } from '../../sku-annotations';
import type { AnnotationPreset } from '../../sku-annotations/types';

export type AnnotationPresetsPanelProps = {
	getCurrentPageNumber: () => number;
	getViewportCenter: () => { x: number; y: number };
	/** The collection a placed preset is added to; defaults to the blueprint-page collection */
	annotations?: SkuAnnotations | null;
	/** Position override, so the SKU-overhead host can keep the panel clear of its own overlays */
	style?: React.CSSProperties;
};

const panelStyle: React.CSSProperties = {
	position: 'absolute',
	top: '8px',
	left: '20px',
	zIndex: 10,
	background: 'rgba(255,255,255,0.95)',
	borderRadius: '4px',
	boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
	fontSize: '11px',
	color: '#333',
	minWidth: '200px',
	maxWidth: '240px'
};

const headerStyle: React.CSSProperties = {
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	padding: '6px 10px',
	cursor: 'pointer',
	fontWeight: 600,
	userSelect: 'none'
};

const rowStyle: React.CSSProperties = {
	display: 'flex',
	alignItems: 'center',
	padding: '4px 8px',
	cursor: 'pointer',
	gap: '6px',
	borderTop: '1px solid #eee'
};

const thumbnailStyle: React.CSSProperties = {
	flex: '0 0 auto',
	background: '#fff',
	border: '1px solid #ddd',
	borderRadius: '2px'
};

const nameStyle: React.CSSProperties = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500 };

const metaStyle: React.CSSProperties = { color: '#888', fontSize: '10px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

const actionStyle: React.CSSProperties = { flex: '0 0 auto', color: '#e53e3e', cursor: 'pointer', padding: '0 2px' };

const typeLabels: Partial<Record<string, string>> = {
	[SkuAnnotationType.TextBox]: 'Text box',
	[SkuAnnotationType.Doodle]: 'Doodle',
	[SkuAnnotationType.Line]: 'Line',
	[SkuAnnotationType.Rectangle]: 'Rectangle',
	[SkuAnnotationType.Ellipse]: 'Ellipse'
};

const THUMBNAIL_WIDTH = 40;
const THUMBNAIL_HEIGHT = 28;

const describePreset = (preset: AnnotationPreset): string => {
	const { annotations, createdAt } = preset;
	const created = new Date(createdAt);
	const datePart = Number.isNaN(created.getTime()) ? '' : ` · ${created.toLocaleDateString()}`;
	if (annotations.length === 1) return `${typeLabels[annotations[0].type ?? ''] ?? 'Shape'}${datePart}`;
	return `${annotations.length} items${datePart}`;
};

const renderThumbnailShape = (a: Partial<SkuAnnotationData>, key: number): React.ReactElement | null => {
	const { type, x = 0, y = 0, x2 = 0, y2 = 0, width = 0, height = 0, color = '#333333', backgroundColor = '#ffffff', fillOpacity = 0, points = [] } = a;
	const stroke = { stroke: color, strokeWidth: 1, vectorEffect: 'non-scaling-stroke' as const };

	if (type === SkuAnnotationType.Line) return <line key={key} x1={x} y1={y} x2={x2} y2={y2} {...stroke} />;
	if (type === SkuAnnotationType.Doodle) {
		if (points.length < 2) return null;
		return <polyline key={key} points={points.map((p) => `${x + p.x},${y + p.y}`).join(' ')} fill="none" {...stroke} />;
	}
	if (type === SkuAnnotationType.Ellipse) {
		return <ellipse key={key} cx={x + (width / 2)} cy={y + (height / 2)} rx={width / 2} ry={height / 2} fill={backgroundColor} fillOpacity={fillOpacity} {...stroke} />;
	}
	if (type === SkuAnnotationType.Rectangle) {
		return <rect key={key} x={x} y={y} width={width} height={height} fill={backgroundColor} fillOpacity={fillOpacity} {...stroke} />;
	}
	return (
		<g key={key}>
			<rect x={x} y={y} width={width} height={height} fill={backgroundColor} stroke="#bbb" strokeWidth={1} vectorEffect="non-scaling-stroke" />
			<line x1={x + (width * 0.15)} y1={y + (height * 0.4)} x2={x + (width * 0.85)} y2={y + (height * 0.4)} stroke="#999" strokeWidth={1} vectorEffect="non-scaling-stroke" />
			<line x1={x + (width * 0.15)} y1={y + (height * 0.65)} x2={x + (width * 0.6)} y2={y + (height * 0.65)} stroke="#999" strokeWidth={1} vectorEffect="non-scaling-stroke" />
		</g>
	);
};

type PresetThumbnailProps = {
	preset: AnnotationPreset;
};

const PresetThumbnail = (props: PresetThumbnailProps): React.ReactElement => {
	const { preset: { annotations } } = props;
	if (annotations.length === 0) return <svg width={THUMBNAIL_WIDTH} height={THUMBNAIL_HEIGHT} style={thumbnailStyle} />;

	const minX = Math.min(...annotations.map(getAnnotationLeftExtent));
	const minY = Math.min(...annotations.map(getAnnotationTopExtent));
	const width = Math.max(1, Math.max(...annotations.map(getAnnotationRightExtent)) - minX);
	const height = Math.max(1, Math.max(...annotations.map(getAnnotationBottomExtent)) - minY);
	const pad = Math.max(width, height) * 0.06;

	return (
		<svg
			width={THUMBNAIL_WIDTH}
			height={THUMBNAIL_HEIGHT}
			viewBox={`${minX - pad} ${minY - pad} ${width + (pad * 2)} ${height + (pad * 2)}`}
			preserveAspectRatio="xMidYMid meet"
			style={thumbnailStyle}
		>
			{annotations.map((a, index) => renderThumbnailShape(a, index))}
		</svg>
	);
};

/**
 * #354 -- the panel inside blueprint view listing saved shape/text-box presets. Clicking one
 * instantiates it re-centered at the current page's viewport center; presets and the save/delete
 * callbacks are all supplied from outside the component package (react-ui owns the API calls,
 * including surfacing a failed save/load as a toast).
 */
export const AnnotationPresetsPanel = (props: AnnotationPresetsPanelProps): React.ReactElement | null => {
	const { getCurrentPageNumber, getViewportCenter, annotations: target = null, style } = props;
	const { controller } = useContext(AppContext) ?? {};
	const kitchen = controller?.kitchen;
	const { annotationPresets, onDeleteAnnotationPreset, setSelectedAnnotationIds } = useSkuAnnotation();
	const [collapsed, setCollapsed] = useState(false);
	const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null);

	if (!kitchen) return null;

	const handlePlace = (presetId: string): void => {
		const preset = annotationPresets.find((p) => p.id === presetId);
		if (!preset) return;
		const { x, y } = getViewportCenter();
		const newIds = placeAnnotationPreset({ kitchen, preset, pageNumber: getCurrentPageNumber(), centerX: x, centerY: y, target });
		if (newIds.length > 0) setSelectedAnnotationIds(newIds);
	};

	const handleDelete = (presetId: string): void => {
		setConfirmingDeleteId(null);
		if (onDeleteAnnotationPreset) onDeleteAnnotationPreset(presetId);
	};

	return (
		<div style={{ ...panelStyle, ...(style ?? {}) }} data-annotation-presets-panel="true">
			<div style={headerStyle} onClick={() => setCollapsed((c) => !c)}>
				<span>Presets ({annotationPresets.length})</span>
				<span>{collapsed ? '▸' : '▾'}</span>
			</div>
			{!collapsed && (
				<div>
					{annotationPresets.length === 0 && (
						<div style={{ padding: '4px 10px 8px', color: '#666' }}>
							No saved presets yet — select a shape or text box, then use the toolbar's save-preset button (or right-click and choose “Save to presets”).
						</div>
					)}
					{annotationPresets.map((preset) => (
						<div key={preset.id} style={rowStyle}>
							<PresetThumbnail preset={preset} />
							<div onClick={() => handlePlace(preset.id)} style={{ flex: 1, minWidth: 0 }} title={`Place "${preset.name}" at the center of this page`}>
								<div style={nameStyle}>{preset.name}</div>
								<div style={metaStyle}>{describePreset(preset)}</div>
							</div>
							{onDeleteAnnotationPreset && confirmingDeleteId !== preset.id && (
								<span onClick={() => setConfirmingDeleteId(preset.id)} style={actionStyle} title="Delete preset">✕</span>
							)}
							{onDeleteAnnotationPreset && confirmingDeleteId === preset.id && (
								<span style={{ flex: '0 0 auto', whiteSpace: 'nowrap' }}>
									<span onClick={() => handleDelete(preset.id)} style={actionStyle} title="Confirm delete">Delete</span>
									<span onClick={() => setConfirmingDeleteId(null)} style={{ ...actionStyle, color: '#3182ce' }} title="Keep preset">Keep</span>
								</span>
							)}
						</div>
					))}
				</div>
			)}
		</div>
	);
};
